import { cloneDeep, isMatch } from 'lodash-es';
import { PrintColor, PrintSettings } from '../../core/settings';
import { _PrintFile, PrintFile } from '../product';

/**
 * Finds the matching thumbnail for a given file based on combined print settings.
 * @param fileUploaded - The uploaded file with available thumbnails.
 * @param settings - The current print settings.
 * @param pFile - The print file instance with individual overrides (coverType, docColor, etc.).
 * @returns The matching thumbnail StoredFile, or null if not found.
 */
export function getThumbFile(fileUploaded: PrintFile, settings: PrintSettings, pFile: _PrintFile = null) {
  const _settings = cloneDeep(settings);
  _settings.color = _settings.docColor || _settings.coverColor ? PrintColor.COLOR : PrintColor.BLACKNWHITE;
  _settings.pagesPerSheet = pFile.coverColor && !pFile.docColor ? 1 : settings.pagesPerSheet;
  delete _settings.coverColor;
  fileUploaded.thumbs?.forEach(th => {
    delete th.printSettings.coverColor;
  });
  if (pFile.coverType) {
    _settings.coverColor = pFile.coverType.coverColor;
    _settings.pagesPerSheet = pFile.coverType.pagesPerSheet;
  }
  const thumb = fileUploaded?.thumbs?.find((thumb) =>
    isMatch(_settings, thumb.printSettings)
  );
  return thumb ? thumb.file : null;
}

/**
 * Extracts the first File from a native `<input type="file">` change event.
 * @param event - The DOM change event from the file input.
 * @returns The selected File, or undefined if no file was selected.
 */
export function getFileFromEvent(event: Event | { target: { files: FileList; }; }): File | undefined {
  return (event as any)?.target?.files?.[0];
}

/**
 * Reads a File as a base64 data URL using FileReader. Useful to preview an
 * image in the UI before uploading it.
 * @param file - The image File to read.
 * @returns A Promise resolving to the data URL string.
 */
export function loadImageAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target?.result as string);
    reader.onerror = () => reject(new Error('Error Loading Image'));
    reader.readAsDataURL(file);
  });
}
